import { Button } from '@dev-spendesk/grapes';
import get from 'lodash/get';
import { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { TokenInput } from 'common/components/TokenInput';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { routes, routeFor } from 'src/core/constants/routes';

import BackgroundCardActivation from './img/background.png';
import { CardDesign } from '../../../components/CardDesign';
import * as cardActions from '../../redux/actions';

import './RecardActivation.css';

type Props = {
  card: {
    id: string;
    addressLine1: string;
    addressLine2: string;
    zipCode: string;
    city: string;
    country: string;
    brand: string;
  };
  fetchPlasticCard: (cardId: string) => void;
  activateCard: (
    recardId: string,
    digits: string,
  ) => Promise<{ outcome: string; card?: Card }>;
};

type Card = {
  card_name: string;
  card_visible_number: string;
  status: string;
  card_exp_date: string;
  activation_status: string;
};

const RecardActivation = ({ card, activateCard, fetchPlasticCard }: Props) => {
  const { t } = useTranslation('global');

  const company = useCompany();

  const [activationStep, setActivationStep] = useState(1);
  const [digitsValue, setDigitsValue] = useState(['', '', '', '']);
  const [activating, setActivating] = useState(false);

  const [newCard, setNewCard] = useState<Card | undefined>();
  const [fetchingNewCard, setFetchingNewCard] = useState(false);
  const [confettiShown, setConfettiShown] = useState(false);

  const activate = useCallback(async () => {
    setActivating(true);
    const recardId: string = get(
      card,
      'recard_request.next_card_order.card_id',
      '',
    );
    const result = await activateCard(recardId, digitsValue.join(''));
    if (result.outcome === 'error') {
      setActivating(false);
    } else {
      setNewCard(result.card);
      setActivationStep(3); // Show success
    }
  }, [activateCard, card, digitsValue]);

  const isValid =
    digitsValue.length === 4 &&
    digitsValue.every((value) => value && value !== '');

  useEffect(() => {
    const onWindowKeydown = (event: KeyboardEvent) => {
      if (
        activationStep === 2 &&
        isValid &&
        event.keyCode === 13 &&
        !activating
      ) {
        activate();
      }
    };
    window.addEventListener('keydown', onWindowKeydown);
    return () => {
      window.removeEventListener('keydown', onWindowKeydown);
    };
  }, [activate, activating, activationStep, isValid]);

  if (activationStep === 1) {
    return (
      <div className="RecardActivation mx-auto mt-32 w-[420px]">
        <h1 className="text-content-primary title-xl mb-8">
          {t('recard.activation.step1.title')}
        </h1>
        <p className="text-content-secondary-bg-primary body-m mb-24">
          {t('recard.activation.step1.subtitle')}
        </p>
        <div className="box">
          <p className="title-m">{t('recard.activation.step1.notice.title')}</p>
          <ul className="body-m mt-24 list-disc">
            <li className="mb-16 ml-24">
              {t('recard.activation.step1.notice.pendingPayments')}
            </li>
            <li className="mb-16 ml-24">
              {t('recard.activation.step1.notice.updateCard')}
            </li>
          </ul>
          <p className="text-content-primary body-s mb-32">
            {t('recard.activation.step1.notice.footnote')}
          </p>
          <div className="flex justify-between">
            <Link to={routeFor(routes.CARD.path, { company: company.id })}>
              <Button
                variant="secondaryNeutral"
                text={t('recard.activation.step1.doLater')}
              />
            </Link>
            <Button
              text={t('recard.activation.step1.continue')}
              onClick={() => setActivationStep(2)}
            />
          </div>
        </div>
      </div>
    );
  }

  if (activationStep === 2) {
    return (
      <div className="RecardActivation mx-auto mt-32 w-[420px]">
        <h1 className="text-content-primary title-xl mb-8">
          {t('recard.activation.step2.title')}
        </h1>
        <p className="text-content-secondary-bg-primary body-m mb-24">
          {t('recard.activation.step2.subtitle1')}
          <br />
          {t('recard.activation.step2.subtitle2')}
        </p>
        <div className="box relative overflow-hidden p-0">
          <div>
            <img
              className="w-full"
              src={BackgroundCardActivation}
              alt="Card activation background"
            />
            <CardDesign
              width={257}
              numbers={`............${digitsValue.join('')}`}
              isActivating
            />
          </div>
          <div className="p-24">
            <h2 className="text-content-primary title-m mb-8">
              {t('recard.activation.step2.enterDigits')}
            </h2>
            <TokenInput
              token={digitsValue}
              tokenLength={4}
              onTokenChange={setDigitsValue}
            />
            <div className="mt-32 flex justify-between">
              <Button
                variant="secondaryNeutral"
                text={t('recard.activation.step2.back')}
                isDisabled={activating}
                onClick={() => {
                  setActivationStep(1);
                  setDigitsValue(['', '', '', '']);
                }}
              />
              <Button
                text={
                  activating
                    ? t('recard.activation.step2.activating')
                    : t('recard.activation.step2.activate')
                }
                onClick={activate}
                isDisabled={activating || !isValid}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!confettiShown) {
    setConfettiShown(true);
  }

  return (
    <div className="RecardActivation mx-auto mt-32 w-[420px]">
      <h1 className="text-content-primary title-xl mb-8">
        {t('recard.activation.step3.title')}
      </h1>
      <p className="text-content-secondary-bg-primary body-m mb-24">
        {t('recard.activation.step3.subtitle')}
      </p>
      <div className="RecardActivated box body-m relative text-center">
        {newCard && (
          <CardDesign width={350} name={newCard.card_name} brand={card.brand} />
        )}
        <p>{t('recard.activation.step3.card.deactivated')}</p>
        <p>{t('recard.activation.step3.card.throw')}</p>
        <Button
          className="mt-24"
          fit="parent"
          text={t('recard.activation.step3.card.show')}
          onClick={() => {
            setFetchingNewCard(true);
            const recardId: string = get(
              card,
              'recard_request.next_card_order.card_id',
              '',
            );
            fetchPlasticCard(recardId);
          }}
          isDisabled={fetchingNewCard}
        />
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  activateCard: cardActions.activateCard,
  fetchPlasticCard: cardActions.fetchPlasticCard,
};

const ConnectedRecardActivation = connect(
  null,
  mapDispatchToProps,
)(RecardActivation);

export { ConnectedRecardActivation as RecardActivation };
